import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2 } from "lucide-react";
import Avatar from "./Avatar";
import { ChatMessages } from "./chat/ChatMessages";
import { ChatComposer } from "./chat/ChatComposer";
import { useChatSession } from "@/hooks/useChatSession";

// Floating Mochi chat: launcher button + slide-up panel.
// Session/API logic lives in useChatSession; message list + composer are
// ./chat/ subcomponents.
export default function ChatWidget({ avatarStyle = "feminine", avatarSkin = null, mood = "happy", botName = "Mochi", onActions, onOpenChange }) {
    const [open, setOpen] = useState(false);
    const { messages, draft, setDraft, sending, loaded, quota, loadHistory, send, clear } =
        useChatSession({ onActions });

    // Bubble open-state up so the parent (App.js) can suppress idle glances
    // / other ambient animations while the user is actively chatting.
    useEffect(() => {
        onOpenChange?.(open);
    }, [open, onOpenChange]);

    useEffect(() => {
        if (open) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <>
            {/* Floating launcher */}
            <AnimatePresence>
                {!open && (
                    <motion.button
                        key="launcher"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 260, damping: 22 }}
                        onClick={() => setOpen(true)}
                        className="fixed right-4 z-40 group"
                        style={{ bottom: "calc(max(env(safe-area-inset-bottom), 0px) + 64px)" }}
                        data-testid="chat-launcher"
                        aria-label="Chat with Mochi"
                    >
                        <span className="absolute -top-2 -right-1 w-3 h-3 rounded-full bg-mustard ring-2 ring-cream animate-soft-pulse" />
                        <div className="w-14 h-14 rounded-full bg-cream shadow-float flex items-center justify-center hover:-translate-y-0.5 transition-all border border-warm-300/40">
                            <Avatar mood={mood} size={50} style={avatarStyle} skin={avatarSkin} />
                        </div>
                        <span className="absolute -top-7 right-1 text-[10px] uppercase tracking-widest font-display text-warm-500 bg-cream px-2 py-0.5 rounded-full shadow-clay opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Chat with {botName}
                        </span>
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Chat panel */}
            <AnimatePresence>
                {open && (
                    <>
                        {/* Mobile backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-40 bg-warm-900/30 backdrop-blur-sm sm:hidden"
                            onClick={() => setOpen(false)}
                        />
                        <motion.div
                            key="panel"
                            initial={{ y: 40, opacity: 0, scale: 0.96 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 40, opacity: 0, scale: 0.96 }}
                            transition={{ type: "spring", stiffness: 260, damping: 24 }}
                            className="fixed z-50 flex flex-col bg-cream shadow-float border border-warm-300/40
                                       left-0 right-0 bottom-0 rounded-t-clay max-h-[90dvh]
                                       sm:left-auto sm:right-5 sm:bottom-5 sm:w-[360px] sm:max-h-[560px] sm:rounded-clay"
                            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
                            data-testid="chat-panel"
                        >
                            {/* Header */}
                            <div className="flex items-center gap-3 p-3 border-b border-warm-300/30">
                                <div className="w-11 h-11 rounded-full bg-surface flex items-center justify-center shrink-0">
                                    <Avatar mood={mood} size={42} style={avatarStyle} skin={avatarSkin} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-display text-warm-900 text-base leading-none">{botName}</p>
                                    <p className="font-body text-[11px] text-warm-500 mt-1 inline-flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-sage" /> always here
                                    </p>
                                </div>
                                {messages.length > 0 && (
                                    <button
                                        onClick={clear}
                                        className="text-warm-500 hover:text-terracotta p-2 rounded-full hover:bg-surface transition-all"
                                        aria-label="Clear chat"
                                        data-testid="chat-clear"
                                        title="Clear conversation"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                                <button
                                    onClick={() => setOpen(false)}
                                    className="text-warm-500 hover:text-warm-900 p-2 rounded-full hover:bg-surface transition-all"
                                    aria-label="Close chat"
                                    data-testid="chat-close"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <ChatMessages
                                messages={messages}
                                loaded={loaded}
                                sending={sending}
                                botName={botName}
                                avatarStyle={avatarStyle}
                                avatarSkin={avatarSkin}
                                mood={mood}
                                onSuggest={setDraft}
                            />

                            <ChatComposer
                                draft={draft}
                                setDraft={setDraft}
                                sending={sending}
                                onSend={send}
                                autoFocus={open}
                                quota={quota}
                            />
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
